import type { AssetRef } from "@kss/schema";
import { useState } from "react";
import { useLoroSelector } from "../../sync/hooks";
import { MediaShell } from "../media/MediaShell";
import { derivativeUrl, useAsset } from "../media/useAsset";
import type { BlockComponentProps } from "../registry";
import { bodyMap } from "../textField";
import { useT } from "../../i18n";

/** Image block (spec §8.8): thumb_1024 → original → local OPFS URL while uploading; lightbox. */
export function ImageBlock({ handle, treeId, node, readOnly }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const asset = (useLoroSelector(handle.doc, () => (body?.get("asset") as AssetRef | null) ?? null, (a, b) => a?.hash === b?.hash) ?? null) as AssetRef | null;
  const alt = useLoroSelector(handle.doc, () => String(body?.get("alt") ?? "")) ?? "";
  const { upload, derivatives, stored, localUrl } = useAsset(asset?.hash ?? null);
  const [open, setOpen] = useState(false);
  if (!body) return null;
  const thumb = derivativeUrl(derivatives, "thumb_1024");
  const src = thumb ?? (stored ? `/api/assets/${asset?.hash}` : localUrl);
  return (
    <MediaShell
      handle={handle}
      treeId={treeId}
      body={body}
      accept="image/*"
      readOnly={readOnly}
      asset={asset}
      upload={upload}
      extraFields={
        <input className="w-full bg-transparent text-xs text-neutral-500 outline-none" placeholder={t("image.alt")} defaultValue={alt} readOnly={readOnly} data-testid="image-alt" onBlur={(e) => { if (e.target.value !== alt) { body.set("alt", e.target.value); handle.doc.commit(); } }} />
      }
    >
      {src ? (
        <img src={src} alt={alt} className="max-h-96 max-w-full cursor-zoom-in rounded" data-testid="image-img" data-fill-media onClick={() => setOpen(true)} />
      ) : (
        <div className="text-sm text-neutral-500">{t("media.offline", { name: asset?.name ?? "" })}</div>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={() => setOpen(false)}>
          <img src={stored ? `/api/assets/${asset?.hash}` : (localUrl ?? src ?? "")} alt={alt} className="max-h-full max-w-full" />
        </div>
      )}
    </MediaShell>
  );
}
